import { backoffice, BackofficeUser } from "@/endpoints/dryp/backoffice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useStore } from "@/hooks/useStore";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useState } from "react";
import { toast } from "sonner";

interface SetPrimaryOrganizationProps {
	user: BackofficeUser;
	onSuccess: () => void;
}

export const SetPrimaryOrganization: FC<SetPrimaryOrganizationProps> = observer(({ user: targetUser, onSuccess }) => {
	const {
		userStore,
		uiStore: { modalStore },
	} = useStore();
	const isOwnUser = userStore.getUser?.name === targetUser.userName;

	const userOrganizationsApi = useAsyncStateStore(backoffice.getUserOrganizations);
	const setPrimaryOrganizationApi = useAsyncStateStore(backoffice.setPrimaryUserOrganization);

	const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

	useEffect(() => {
		if (isOwnUser) return;
		userOrganizationsApi.fetch(targetUser.id);
	}, []);

	const assignments = userOrganizationsApi.data || [];
	const currentPrimaryOrganizationId =
		assignments.find(assignment => assignment.primaryOrganization)?.organizationId || "";

	useEffect(() => {
		setSelectedOrganizationId(currentPrimaryOrganizationId);
	}, [currentPrimaryOrganizationId]);

	const handleSave = async () => {
		try {
			await setPrimaryOrganizationApi.fetch(targetUser.id, selectedOrganizationId);
			modalStore.close();
			onSuccess();
			toast.success("Primary organization updated");
		} catch {
			toast.error("Failed to update primary organization");
		}
	};

	const hasChanged = selectedOrganizationId !== "" && selectedOrganizationId !== currentPrimaryOrganizationId;

	return (
		<>
			<DialogHeader>
				<DialogTitle>Change Primary Organization - {targetUser.userName}</DialogTitle>
				<DialogDescription className="sr-only">
					Change the primary organization for {targetUser.userName}.
				</DialogDescription>
			</DialogHeader>

			{isOwnUser ? (
				<p className="text-sm text-muted-foreground py-4">
					You cannot change your own primary organization. Please contact customer services for assistance.
				</p>
			) : userOrganizationsApi.loading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : userOrganizationsApi.error ? (
				<p className="text-sm text-destructive py-4">Something went wrong.</p>
			) : assignments.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4">No organizations assigned.</p>
			) : (
				<div className="flex flex-col gap-4">
					<p className="text-sm text-muted-foreground">
						Only organizations already assigned to {targetUser.userName} appear here — to switch to a
						different one, add it first via “Manage Organizations”. Once the primary has been changed, the
						previous organization can be removed.
					</p>

					<Select
						value={selectedOrganizationId}
						onValueChange={setSelectedOrganizationId}
						disabled={setPrimaryOrganizationApi.loading}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Select an organization" />
						</SelectTrigger>
						<SelectContent>
							{assignments.map(assignment => (
								<SelectItem key={assignment.organizationId} value={assignment.organizationId}>
									<span className="flex items-center gap-2">
										<span className="truncate">{assignment.organizationName}</span>
										{assignment.primaryOrganization && <Badge variant="secondary">Current</Badge>}
										<span className="text-xs text-muted-foreground">{assignment.roleName}</span>
									</span>
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<p className="text-sm text-muted-foreground">
						Changing the primary organization signs {targetUser.userName} out of all sessions.
					</p>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => modalStore.close()}
							disabled={setPrimaryOrganizationApi.loading}
						>
							Cancel
						</Button>
						<Button onClick={handleSave} disabled={!hasChanged || setPrimaryOrganizationApi.loading}>
							{setPrimaryOrganizationApi.loading ? "Saving" : "Save"}
						</Button>
					</DialogFooter>
				</div>
			)}
		</>
	);
});
